import {z} from 'zod'

import {apiFetch, downloadApiBlob} from '../../api/fetch'
import {
    type CreateRentalAssetInput,
    createRentalAssetRequestSchema,
    type CreateRentalAssetTypeInput,
    createRentalAssetTypeRequestSchema,
    type CreateThirdPartyRentalAssetOfferingInput,
    createThirdPartyRentalAssetOfferingRequestSchema,
    type CreateThirdPartyRentalCompanyInput,
    createThirdPartyRentalCompanyRequestSchema,
    type EquipmentStockSummariesList,
    equipmentStockSummariesListSchema,
    type EquipmentStockSummary,
    equipmentStockSummarySchema,
    type EquipmentTypeFleet,
    equipmentTypeFleetSchema,
    identifiedRentalAssetSchema,
    type ListRentalAssetAssignmentsOptions,
    type ListRentalAssetsOptions,
    type ListRentalAssetTypesOptions,
    type ListThirdPartyRentalAssetOfferingsOptions,
    type RentalAsset,
    type RentalAssetAssignmentList,
    rentalAssetAssignmentListSchema,
    type RentalAssetImportPreview,
    rentalAssetImportPreviewSchema,
    type RentalAssetImportResult,
    rentalAssetImportResultSchema,
    rentalAssetSchema,
    type RentalAssetType,
    rentalAssetTypeSchema,
    type StockLevelAdjustmentInput,
    stockLevelAdjustmentRequestSchema,
    type StockLevelAdjustmentResponse,
    stockLevelAdjustmentResponseSchema,
    type ThirdPartyRentalAssetOffering,
    thirdPartyRentalAssetOfferingSchema,
    type ThirdPartyRentalCompany,
    thirdPartyRentalCompanySchema,
    type UpdateRentalAssetInput,
    updateRentalAssetRequestSchema,
    type UpdateRentalAssetTypeInput,
    updateRentalAssetTypeRequestSchema,
    type UpdateThirdPartyRentalAssetOfferingInput,
    updateThirdPartyRentalAssetOfferingRequestSchema,
    type UpdateThirdPartyRentalCompanyInput,
    updateThirdPartyRentalCompanyRequestSchema,
} from './schemas'

export async function listRentalAssetTypes(
    options?: ListRentalAssetTypesOptions,
): Promise<RentalAssetType[]> {
    const query = options?.includeArchived ? '?include_archived=true' : ''
    const raw = await apiFetch<unknown>(`/rental-asset-types${query}`)
    return z.array(rentalAssetTypeSchema).parse(raw)
}

export async function getRentalAssetType(id: string): Promise<RentalAssetType> {
    const raw = await apiFetch<unknown>(`/rental-asset-types/${id}`)
    return rentalAssetTypeSchema.parse(raw)
}

export async function createRentalAssetType(
    input: CreateRentalAssetTypeInput,
): Promise<RentalAssetType> {
    const body = createRentalAssetTypeRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/rental-asset-types', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return rentalAssetTypeSchema.parse(raw)
}

export async function updateRentalAssetType(
    id: string,
    input: UpdateRentalAssetTypeInput,
): Promise<RentalAssetType> {
    const body = updateRentalAssetTypeRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/rental-asset-types/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return rentalAssetTypeSchema.parse(raw)
}

export async function archiveRentalAssetType(id: string): Promise<RentalAssetType> {
    const raw = await apiFetch<unknown>(`/rental-asset-types/${id}/archive`, {method: 'POST'})
    return rentalAssetTypeSchema.parse(raw)
}

export async function unarchiveRentalAssetType(id: string): Promise<RentalAssetType> {
    const raw = await apiFetch<unknown>(`/rental-asset-types/${id}/unarchive`, {method: 'POST'})
    return rentalAssetTypeSchema.parse(raw)
}

export async function listEquipmentStockSummaries(): Promise<EquipmentStockSummariesList> {
    const raw = await apiFetch<unknown>('/equipment-stock-summaries')
    return equipmentStockSummariesListSchema.parse(raw)
}

export async function getStockSummary(typeId: string): Promise<EquipmentStockSummary> {
    const raw = await apiFetch<unknown>(`/rental-asset-types/${typeId}/stock-summary`)
    return equipmentStockSummarySchema.parse(raw)
}

export async function adjustStockLevel(
    typeId: string,
    input: StockLevelAdjustmentInput,
): Promise<StockLevelAdjustmentResponse> {
    const body = stockLevelAdjustmentRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/rental-asset-types/${typeId}/stock-level`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
    return stockLevelAdjustmentResponseSchema.parse(raw)
}

export async function getEquipmentTypeFleet(typeId: string): Promise<EquipmentTypeFleet> {
    const raw = await apiFetch<unknown>(`/rental-asset-types/${typeId}/fleet`)
    return equipmentTypeFleetSchema.parse(raw)
}

export async function listRentalAssets(options?: ListRentalAssetsOptions): Promise<RentalAsset[]> {
    const params = new URLSearchParams()
    if (options?.rentalAssetTypeId) {
        params.set('rental_asset_type_id', options.rentalAssetTypeId)
    }
    if (options?.includeArchived) {
        params.set('include_archived', 'true')
    }
    const query = params.toString()
    const raw = await apiFetch<unknown>(`/rental-assets${query ? `?${query}` : ''}`)
    return z.array(identifiedRentalAssetSchema).parse(raw)
}

export async function getRentalAsset(id: string): Promise<RentalAsset> {
    const raw = await apiFetch<unknown>(`/rental-assets/${id}`)
    return rentalAssetSchema.parse(raw)
}

export async function listRentalAssetAssignments(
    assetId: string,
    options: ListRentalAssetAssignmentsOptions,
): Promise<RentalAssetAssignmentList> {
    const query = new URLSearchParams({
        date_from: options.dateFrom,
        date_to: options.dateTo,
    })
    const raw = await apiFetch<unknown>(`/rental-assets/${assetId}/assignments?${query}`)
    return rentalAssetAssignmentListSchema.parse(raw)
}

export async function createRentalAsset(input: CreateRentalAssetInput): Promise<RentalAsset> {
    const body = createRentalAssetRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/rental-assets', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return rentalAssetSchema.parse(raw)
}

export async function updateRentalAsset(
    id: string,
    input: UpdateRentalAssetInput,
): Promise<RentalAsset> {
    const body = updateRentalAssetRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/rental-assets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return rentalAssetSchema.parse(raw)
}

export async function archiveRentalAsset(id: string): Promise<RentalAsset> {
    const raw = await apiFetch<unknown>(`/rental-assets/${id}/archive`, {method: 'POST'})
    return rentalAssetSchema.parse(raw)
}

export async function unarchiveRentalAsset(id: string): Promise<RentalAsset> {
    const raw = await apiFetch<unknown>(`/rental-assets/${id}/unarchive`, {method: 'POST'})
    return rentalAssetSchema.parse(raw)
}

export async function listThirdPartyRentalCompanies(): Promise<ThirdPartyRentalCompany[]> {
    const raw = await apiFetch<unknown>('/third-party-rental-companies')
    return z.array(thirdPartyRentalCompanySchema).parse(raw)
}

export async function createThirdPartyRentalCompany(
    input: CreateThirdPartyRentalCompanyInput,
): Promise<ThirdPartyRentalCompany> {
    const body = createThirdPartyRentalCompanyRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/third-party-rental-companies', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return thirdPartyRentalCompanySchema.parse(raw)
}

export async function updateThirdPartyRentalCompany(
    id: string,
    input: UpdateThirdPartyRentalCompanyInput,
): Promise<ThirdPartyRentalCompany> {
    const body = updateThirdPartyRentalCompanyRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/third-party-rental-companies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return thirdPartyRentalCompanySchema.parse(raw)
}

export async function deleteThirdPartyRentalCompany(id: string): Promise<void> {
    await apiFetch<void>(`/third-party-rental-companies/${id}`, {
        method: 'DELETE',
    })
}

function offeringsQuery(options: ListThirdPartyRentalAssetOfferingsOptions = {}) {
    const params = new URLSearchParams()
    if (options.includeArchived) {
        params.set('include_archived', 'true')
    }
    if (options.rentalAssetTypeId) {
        params.set('rental_asset_type_id', options.rentalAssetTypeId)
    }
    if (options.companyId) {
        params.set('company_id', options.companyId)
    }
    const query = params.toString()
    return query ? `?${query}` : ''
}

export async function listThirdPartyRentalAssetOfferings(
    options: ListThirdPartyRentalAssetOfferingsOptions = {},
): Promise<ThirdPartyRentalAssetOffering[]> {
    const raw = await apiFetch<unknown>(
        `/third-party-rental-asset-offerings${offeringsQuery(options)}`,
    )
    return z.array(thirdPartyRentalAssetOfferingSchema).parse(raw)
}

export async function createThirdPartyRentalAssetOffering(
    input: CreateThirdPartyRentalAssetOfferingInput,
): Promise<ThirdPartyRentalAssetOffering> {
    const body = createThirdPartyRentalAssetOfferingRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/third-party-rental-asset-offerings', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return thirdPartyRentalAssetOfferingSchema.parse(raw)
}

export async function updateThirdPartyRentalAssetOffering(
    id: string,
    input: UpdateThirdPartyRentalAssetOfferingInput,
): Promise<ThirdPartyRentalAssetOffering> {
    const body = updateThirdPartyRentalAssetOfferingRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/third-party-rental-asset-offerings/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return thirdPartyRentalAssetOfferingSchema.parse(raw)
}

export async function archiveThirdPartyRentalAssetOffering(
    id: string,
): Promise<ThirdPartyRentalAssetOffering> {
    const raw = await apiFetch<unknown>(`/third-party-rental-asset-offerings/${id}/archive`, {
        method: 'POST',
    })
    return thirdPartyRentalAssetOfferingSchema.parse(raw)
}

export async function unarchiveThirdPartyRentalAssetOffering(
    id: string,
): Promise<ThirdPartyRentalAssetOffering> {
    const raw = await apiFetch<unknown>(`/third-party-rental-asset-offerings/${id}/unarchive`, {
        method: 'POST',
    })
    return thirdPartyRentalAssetOfferingSchema.parse(raw)
}

export async function downloadRentalAssetImportTemplate(): Promise<void> {
    await downloadApiBlob(
        '/rental-assets/import/template',
        'first-party-assets-import-template.csv',
    )
}

export async function previewRentalAssetImport(file: File): Promise<RentalAssetImportPreview> {
    const formData = new FormData()
    formData.append('file', file)

    const raw = await apiFetch<unknown>('/rental-assets/import/preview', {
        method: 'POST',
        body: formData,
    })
    return rentalAssetImportPreviewSchema.parse(raw)
}

export async function importRentalAssetsCsv(file: File): Promise<RentalAssetImportResult> {
    const formData = new FormData()
    formData.append('file', file)

    const raw = await apiFetch<unknown>('/rental-assets/import', {
        method: 'POST',
        body: formData,
    })
    return rentalAssetImportResultSchema.parse(raw)
}
